/**
 * Client UX timing for sitter email verification — GrowthBook WHEN only.
 *
 * Flag `sc-sitter-email-verification-placement`:
 * - `registration`               → Variant A (gate during onboarding)
 * - `parent_interaction`         → Variant B (gate at message / apply)
 * - `no_verification_required`   → Variant C (experiment control)
 *
 * This module decides only when the SPA should show the verify-email UX.
 * Empire access policies remain the source of truth on write denials — do not
 * treat this as a second full copy of the backend policy engine.
 *
 * Safe fallback: unknown values → `parent_interaction`.
 */
import { getFeatureValue, syncGrowthbookIdentity } from '@/lib/growthbook';

/** Variant A — verify during sitter onboarding / registration. */
export const PLACEMENT_REGISTRATION = 'registration';

/** Variant B (also the safe default for unknowns) — verify at message reply or job apply. */
export const PLACEMENT_PARENT_INTERACTION = 'parent_interaction';

/** Variant C — do not force email verification (experiment control). */
export const PLACEMENT_NO_VERIFICATION_REQUIRED = 'no_verification_required';

/** GrowthBook feature key — keep in sync with the Sittercity GB project. */
export const EMAIL_VERIFICATION_PLACEMENT_FEATURE_KEY = 'sc-sitter-email-verification-placement';

export type KnownEmailVerificationPlacement =
  | typeof PLACEMENT_REGISTRATION
  | typeof PLACEMENT_PARENT_INTERACTION
  | typeof PLACEMENT_NO_VERIFICATION_REQUIRED;

export type EmailVerificationPlacement = KnownEmailVerificationPlacement | string;

/** Checkpoint where a caller is asking "must they verify before continuing?" */
export type EmailVerificationMoment = 'onboarding' | 'parent_interaction';

export type EmailVerificationPolicyInput = {
  verified: boolean;
  isSitter: boolean;
  placement: EmailVerificationPlacement;
  moment: EmailVerificationMoment;
};

/** Coerce GB / caller values to a known placement; unknown → parent_interaction. */
export function normalizeEmailVerificationPlacement(placement: EmailVerificationPlacement): KnownEmailVerificationPlacement {
  if (placement === PLACEMENT_REGISTRATION) {
    return PLACEMENT_REGISTRATION;
  }

  if (placement === PLACEMENT_NO_VERIFICATION_REQUIRED) {
    return PLACEMENT_NO_VERIFICATION_REQUIRED;
  }

  return PLACEMENT_PARENT_INTERACTION;
}

/**
 * Full sitter onboarding SPA paths where invited sitters re-enter the experiment
 * even if the backend still reports express exclusion (before profile.begin!).
 */
export function isFullSitterOnboardingPath(path?: string | null): boolean {
  if (!path) {
    return false;
  }

  const pathname = path.split('?')[0];
  return pathname === '/sitter/signup/care-type' || pathname.startsWith('/sitter/signup/profile/');
}

/**
 * Current GrowthBook assignment for this user (sticky via userId attributes).
 * Syncs identity first so post-registration reads are not stuck on a null userId.
 * Returns the normalized placement; never throws if the SDK is unavailable.
 *
 * Callers must not invoke this for express-invite sitters who are still excluded
 * from the experiment — evaluating the flag assigns them into A/B/C.
 */
export function getEmailVerificationPlacement(): KnownEmailVerificationPlacement {
  syncGrowthbookIdentity();
  const raw = getFeatureValue(EMAIL_VERIFICATION_PLACEMENT_FEATURE_KEY, PLACEMENT_PARENT_INTERACTION);

  return normalizeEmailVerificationPlacement(raw);
}

/**
 * Client UX gate for the moments we currently enforce in sc-web:
 * - verified users and non-sitters never need this flow
 * - Variant A: onboarding (with parent-interaction fallback if they skip)
 * - Variant B: message / job-apply interaction
 * - Variant C: never force verification
 */
export function shouldRequireEmailVerificationNow(input: EmailVerificationPolicyInput): boolean {
  if (input.verified || !input.isSitter) {
    return false;
  }

  const placement = normalizeEmailVerificationPlacement(input.placement);
  if (placement === PLACEMENT_NO_VERIFICATION_REQUIRED) {
    return false;
  }

  if (input.moment === 'onboarding') {
    return placement === PLACEMENT_REGISTRATION;
  }

  return true;
}
